/**
 * Atribuição da Meta que sobrevive ao banner de cookies.
 *
 * O Pixel só é injetado depois que o visitante aceita os cookies
 * (`analytics-loader.tsx`). Quem ignora o banner — a maioria — nunca gera os
 * cookies `_fbc` e `_fbp`, e o checkout acabava gravando string vazia na
 * metadata do Stripe. A Conversions API recebia a venda sem nenhum
 * identificador de clique e a qualidade de correspondência ia ao chão: a Meta
 * não conseguia ligar a compra ao anúncio que a gerou.
 *
 * O `fbclid` é de natureza diferente dos dois cookies. Não é um rastreador de
 * terceiros carregado por script externo: é um parâmetro que a própria pessoa
 * trouxe na URL quando clicou no anúncio. Guardamos ele em primeira parte, no
 * mesmo formato que a Meta documenta para `_fbc`, para que a CAPI tenha como
 * fechar o círculo mesmo sem o Pixel ter rodado.
 *
 * Se a decisão jurídica for não guardar nem isso antes do consentimento, o
 * único ponto a mudar é a chamada de `captureFbclid()` em `main.tsx` — mova-a
 * para dentro do `AnalyticsLoader`. O resto do arquivo continua correto, só
 * passa a valer para menos gente.
 */

const FBC_STORAGE_KEY = "ecclesiato-fbc";

/** A Meta considera o `_fbc` válido por 90 dias após o clique. */
const FBC_TTL_MS = 90 * 24 * 60 * 60 * 1000;

interface StoredFbc {
  value: string;
  createdAt: number;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(^| )" + name + "=([^;]+)"),
  );
  return match ? match[2] : null;
}

/**
 * Formato exigido pela Meta: `fb.<subdomainIndex>.<creationTime>.<fbclid>`.
 * O índice 1 corresponde a domínio de segundo nível (ecclesiato.com.br), que é
 * onde o cookie é gravado.
 */
function buildFbc(fbclid: string, createdAt: number): string {
  return `fb.1.${createdAt}.${fbclid}`;
}

function readStoredFbc(): StoredFbc | null {
  try {
    const raw = localStorage.getItem(FBC_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredFbc;
    if (!parsed?.value || typeof parsed.createdAt !== "number") return null;

    // Clique velho não atribui mais nada; mandar para a CAPI só sujaria o dado.
    if (Date.now() - parsed.createdAt > FBC_TTL_MS) {
      localStorage.removeItem(FBC_STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Lê o `fbclid` da URL de entrada e guarda o `_fbc` correspondente.
 *
 * Chamada uma vez na inicialização do app, antes de qualquer renderização: o
 * parâmetro só existe na primeira URL da sessão e some assim que a pessoa
 * navega para outra tela.
 */
export function captureFbclid(): void {
  if (typeof window === "undefined") return;

  try {
    const fbclid = new URLSearchParams(window.location.search).get("fbclid");
    if (!fbclid) return;

    // O cookie do Pixel é sempre mais confiável que o nosso: ele reflete o
    // clique que a Meta de fato registrou. Se ele existe, não sobrescreve.
    if (readCookie("_fbc")) return;

    const createdAt = Date.now();
    const value = buildFbc(fbclid, createdAt);

    localStorage.setItem(
      FBC_STORAGE_KEY,
      JSON.stringify({ value, createdAt } satisfies StoredFbc),
    );

    // Grava também no cookie que o Pixel procura. Se a pessoa aceitar os
    // cookies mais tarde, numa página em que o `fbclid` já não está na URL, o
    // Pixel encontra o clique original em vez de começar do zero.
    const isSecure = window.location.protocol === "https:";
    document.cookie = [
      `_fbc=${value}`,
      "path=/",
      `max-age=${Math.floor(FBC_TTL_MS / 1000)}`,
      "SameSite=Lax",
      isSecure ? "Secure" : "",
    ]
      .filter(Boolean)
      .join("; ");
  } catch {
    // Navegador com armazenamento bloqueado. Atribuição é melhor-esforço:
    // nunca deve derrubar o carregamento do app.
  }
}

/**
 * Identificador do clique no anúncio, para a Conversions API.
 * Prefere o cookie do Pixel; cai no que capturamos do `fbclid`.
 */
export function getFbc(): string {
  return readCookie("_fbc") || readStoredFbc()?.value || "";
}

/**
 * Identificador do navegador, criado exclusivamente pelo Pixel.
 *
 * Não há substituto de primeira parte aqui: um `_fbp` inventado por nós nunca
 * foi visto pela Meta e não corresponderia a ninguém. Sem consentimento, este
 * campo vai vazio mesmo — e é por isso que o reforço de correspondência do
 * lado do servidor (e-mail, telefone, external_id) carrega o peso.
 */
export function getFbp(): string {
  return readCookie("_fbp") || "";
}
